(ns practitest.frontend.common-entities
  (:require
    [clojure.string :as str]
    [practitest.frontend.comment-tab :as comment-tab]
    [practitest.frontend.common.dom :as dom]
    [practitest.frontend.common.re-frame :refer [partial-dispatch]]
    [practitest.frontend.common.utils :as utils]
    [practitest.frontend.common-entities.metadata :as metadata]
    [practitest.frontend.components.grid.events :as grid.events]
    [practitest.frontend.components.grid.localstorage :as grid-storage]
    [practitest.frontend.components.grid.sessionstorage :as grid-session-storage]
    [practitest.frontend.components.grid.server :as server]
    [practitest.frontend.components.grid.subs :as grid.subs]
    [practitest.frontend.events :as events]
    [practitest.frontend.gauge-chart :as gauge-chart]
    [practitest.frontend.pie-chart :as pie-chart]
    [practitest.frontend.bar-chart :as bar-chart]
    [practitest.frontend.planned-vs-executed-chart :as planned-vs-executed-chart]
    [practitest.frontend.gauge-chart-config :as gauge-chart-config]
    [practitest.frontend.gherkin-textarea :as gherkin-textarea]
    [practitest.frontend.init :as init]
    [practitest.frontend.routing.router :as router]
    [practitest.frontend.subs :as subs]
    [practitest.frontend.common.rules-engine]
    [practitest.frontend.components2.dashboard.events :as dashboard]
    [practitest.frontend.routing.backend-routes :as broutes]
    [re-frame.core :as re-frame]
    [reagent.core :as reagent]))

(re-frame/reg-sub
  ::data
  (fn [db [_ entity-type state-key]]
    (get-in db [:entity entity-type state-key :data])))

(defn- on-per-page-changed
  [entity-type state-key table-id new-per-page]
  (grid-storage/save-per-page table-id new-per-page)
  ;; Workaround for current select-all implementation
  ;; TODO: remove after new UX is agreed on
  (re-frame/dispatch [::grid.events/clear-selection [entity-type state-key]]))

(defn project-table-id [table-id]
  (let [project-id (:id @(re-frame/subscribe [::subs/current-project]))]
    (str project-id "-" table-id)))

(defn current-page-from-url [] 
  (some->
    (router/get-current-query-params)
    :page
    js/parseInt))

(defn common-index-grid-props
  [entity-type state-key table-id]
  (let [instance-view? (= state-key :testsets-instances-grid)
        table-id (project-table-id table-id)]
    {:fields                @(re-frame/subscribe [::subs/fields])
     :selected?             #(re-frame/subscribe [::grid.subs/selected? [entity-type state-key] %])
     :all-selected?         #(re-frame/subscribe [::grid.subs/all-selected? [entity-type state-key] %])
     :selection             @(re-frame/subscribe [::grid.subs/selection [entity-type state-key]])
     :clear-selection       (partial-dispatch [::grid.events/clear-selection [entity-type state-key]])
     :select-all            (partial-dispatch [::grid.events/select-all [entity-type state-key]])
     :current-view          @(re-frame/subscribe [::grid.subs/current-view entity-type state-key])
     :current-page          @(re-frame/subscribe [::grid.subs/entities-current-page entity-type state-key])
     :total-count           @(re-frame/subscribe [::grid.subs/entities-total-count entity-type state-key])
     :full-data?            @(re-frame/subscribe [::grid.subs/entities-full-data? entity-type state-key])
     :expanded?             @(re-frame/subscribe [::grid.subs/expanded? entity-type state-key])
     :data                  @(re-frame/subscribe [::data entity-type state-key])
     :loading?              @(re-frame/subscribe [::subs/progress? ::fetch-entity-list])
     :show-edit-columns?    false
     :resizable?            true
     :per-page-save         (reagent/partial on-per-page-changed entity-type state-key table-id)
     :current-page-load     (reagent/partial grid-storage/load-current-page table-id)
     :current-page-save     (reagent/partial grid-storage/save-current-page table-id)
     :save-in-url?          true
     :per-page-load         (reagent/partial grid-storage/load-per-page table-id)
     :columns-settings-save (reagent/partial grid-storage/save-column-settings table-id)
     :columns-settings-load (reagent/partial grid-storage/load-column-settings table-id)
     :sort-order-save       (reagent/partial grid-session-storage/save-sort-order table-id)
     :sort-order-load       (reagent/partial grid-session-storage/load-sort-order table-id)
     :autofilters-save      (reagent/partial grid-session-storage/save-autofilters table-id)
     :autofilters-load      (reagent/partial grid-session-storage/load-autofilters table-id)
     :columns-widths-save   (reagent/partial grid-storage/save-column-sizes table-id)
     :columns-widths-load   (reagent/partial grid-storage/load-column-sizes table-id)
     ;; We assume that every entity has display_id column (and it's true for now)
     :default-sort-settings (if instance-view? [:suite_name :asc] [:display_id :desc])}))

(defn sort-column-by-filter-order [column-names columns]
  "Sorts columns based on the order specified in column-names vector.
  The output is a list of the grid columns(maps), ordered by the definition of the custom view"
  (let [field-order-map (zipmap column-names (range))]
    (sort-by (fn [field]
               "Take :field key of field and get its order from field-order-map"
               (let [field (name (:field field))]
                 (get field-order-map field)))
             columns)))

(defn grid-columns [columns filter-column-names]
  (let [grid-columns (filter
                       #(contains? (set filter-column-names) (name (:field %)))
                       columns)
        sorted-columns (sort-column-by-filter-order filter-column-names grid-columns)]
    sorted-columns))

(defn get-entity-columns
  [entity-type state-key]
  (let [entity-columns (get-in metadata/entities-metadata [entity-type :columns-id])
        default-columns (get-in metadata/entities-metadata [entity-type :default-columns])
        columns-sub (re-frame/subscribe [::grid.subs/columns [entity-type entity-columns]])
        filter-cols-sub (re-frame/subscribe [::grid.subs/filter-cols entity-type state-key])
        additional-columns (grid-columns @columns-sub @filter-cols-sub)
        all-columns (concat default-columns additional-columns)]
    all-columns))

(defn prepare-full-selection-ids
  "Special function to prepare current selection IDs adjusted for current view/folder.

  See `lib/multiple_entities.rb` -> `batch_selector` in `practitest-app` for corresponding backend logic"
  [selection current-view]
  (if (grid.subs/full-selection? selection)
    (cond
      ;; All entities mode
      (or (nil? current-view)
          (= "_DEFAULT_" (:id current-view)))
      "_all"

      (:auto_filter current-view)
      (let [{:keys [field ancestry sub_field]} current-view] 
          (let [fields (:field current-view)
                values (:auto_filter current-view)
                fields-array (if (vector? fields) fields [fields])
                fields-parts (str/join "/" (filter some? fields-array))
                values-parts (str/join "/" (filter some? values))]
            (str "_auto_filter:" fields-parts "|" ancestry "|" values-parts)))

      (= :folder (:type current-view))
      (str "_f:" (:id current-view))

      ;; CV mode
      :else
      (str "_cv:" (:id current-view)))
    (str/join "," selection)))

(defn batch-navigate!
  ([path selection]
   (batch-navigate! path selection nil))
  ([path selection current-view]
   (batch-navigate! path selection current-view nil))
  ([path selection current-view optional-params]
   (let [fixed-params {:ids (prepare-full-selection-ids selection current-view)
                       :back_to_url (js/encodeURIComponent (dom/get-current-href-relative))}
         all-params (merge fixed-params optional-params)
         query-string (str/join "&" (map (fn [[k v]] (str (name k) "=" v)) all-params))]
     (dom/navigate! (str path "?" query-string)))))

(defn batch-open-in-new-tab!
  ([path selection]
   (batch-open-in-new-tab! path selection nil))
  ([path selection current-view]
   (batch-open-in-new-tab! path selection current-view nil))
  ([path selection current-view optional-params]
   (let [fixed-params {:ids (prepare-full-selection-ids selection current-view)}
         all-params (merge fixed-params optional-params)
         query-string (str/join "&" (map (fn [[k v]] (str (name k) "=" v)) all-params))]
     (dom/open-in-new-tab! (str path "?" query-string)))))

(defn build-filter [{:keys [id field ancestry auto_filter]}]
  (when (some? id)
    (if auto_filter
      ;; Always use multi-level auto filters (fields + values)
      {:auto_filter (utils/remove-nils {:parent ancestry
                                        :fields field
                                        :values auto_filter})}
      {:cv id})))

(defn get-entity-index [entities current-id]
  (some (fn [[i entity]]
          (when (= (:id entity) current-id)
            i))
        (map-indexed vector entities)))

(defn build-grid-query
  [session-key per-page current-page]
  (let [local-state-updating
        (reagent/atom
          {:sort-settings     (grid-session-storage/load-sort-order session-key)
           :autofilters-state (grid-session-storage/load-autofilters session-key)
           :per-page          per-page})
        tableprops {:local-state-updating  local-state-updating
                    :fields                @(re-frame/subscribe [::subs/fields])}
        current-page (or current-page 1)]
    (server/build-query tableprops current-page)))

(defn ^:export init-page [webcomponent options]
  (init/init! {:options       options
               :webcomponent  webcomponent
               :init-event-fn (fn [config]
                                (let [scenario-steps (or (:scenario config) "")]
                                  [::events/initialize-db
                                   config
                                   (keyword (:entity-key config))
                                   [[::gherkin-textarea/set-steps scenario-steps]]]))}))

;; Per-comment init function
(defn ^:export init-comment [webcomponent options]
  (let [{:keys [data-comments-uri
                data-entity-id
                data-entity-type
                data-permission]
         :as   attrs} (init/attributes->map webcomponent)]

    (init/init-ui! {:options      options
                    :webcomponent webcomponent
                    :root-view    comment-tab/root-view})

    ;; Initial fetch on comment initialisation
    (let [entity-type (keyword data-entity-type)
          entity-id data-entity-id
          entity-permission data-permission]
      (re-frame/dispatch [::comment-tab/fetch-comments data-comments-uri entity-type entity-id]))))

(defn ^:export init-gauge-chart [webcomponent options]
  (let [{:keys [data-item-id
                data-percentage
                data-title
                data-areas]
         :as   attrs} (init/attributes->map webcomponent)]

    (init/init-ui! {:options      options
                    :webcomponent webcomponent
                    :root-view    gauge-chart/root-view})))

(defn ^:export init-pie-chart [webcomponent options]
  (let [attrs      (init/attributes->map webcomponent)
        {:keys [data-item-id
                data-fetch-url]} attrs]
    (re-frame/dispatch [::dashboard/load-pie-data data-fetch-url])
    (init/init-ui! {:options      options
                    :webcomponent webcomponent
                    :root-view    pie-chart/root-view})))

(defn ^:export init-bar-chart [webcomponent options]
  (let [attrs      (init/attributes->map webcomponent)
        {:keys [data-item-id
                data-fetch-url]} attrs]
    (re-frame/dispatch [::dashboard/load-bar-data data-fetch-url])
    (init/init-ui! {:options      options
                    :webcomponent webcomponent
                    :root-view    bar-chart/root-view})))

(defn ^:export init-planned-vs-executed-chart [webcomponent options]
  (let [{:keys [data-fetch-url]}  (init/attributes->map webcomponent)]
    (re-frame/dispatch [::dashboard/load-planned-vs-executed-data data-fetch-url])
    (init/init-ui! {:options      options
                    :webcomponent webcomponent
                    :root-view    planned-vs-executed-chart/root-view})))

(defn ^:export init-gauge-chart-config [webcomponent options]
  (let [{:keys [data-item-id
                data-gauge-config]
         :as   attrs} (init/attributes->map webcomponent)]

    (init/init-ui! {:options      options
                    :webcomponent webcomponent
                    :root-view    gauge-chart-config/root-view})))

(defn ^:export init-gherkin [webcomponent options]
  (init/init-ui! {:options       options
                  :webcomponent  webcomponent
                  :root-view     gherkin-textarea/gherkin-textarea-panel}))

(utils/register-webcomponent! "comment-entities-component")
(utils/register-webcomponent! "gauge-chart-config-component")
